import { useMemo, useState } from "react";
import {
  HiCheck,
  HiChevronDown,
  HiChevronRight,
  HiOutlineCash,
  HiOutlineClipboardList,
  HiOutlineSearch,
  HiOutlineSparkles,
  HiOutlineViewGrid,
} from "react-icons/hi";
import { formatToReal } from "../../utils/utils";
import "./CardQueryDescription.css";

/**
 * CardQueryDescription — layout clean em duas lâminas
 * - Lâmina esquerda: formulário
 * - Lâmina direita: resumo do produto (descrição / tags) em card simples
 * - Adicionais: bloco full-width abaixo, agrupado por categoria
 * - Cabeçalho mínimo com kicker e título
 */
export default function CardQueryDescription({
  title,
  subtitle = "",
  infos = [],
  description = "",
  additionalOptions = [],
  selectedAdditionalIds = [],
  onChangeAdditionalIds = () => {},
  logoOverride,
  kicker = "Consulta",
  children,
  tags = [],
}) {
  const hasAdds =
    Array.isArray(additionalOptions) && additionalOptions.length > 0;
  const hasDescription = Boolean((description || "").trim());

  const selectedSet = useMemo(
    () => new Set((selectedAdditionalIds || []).map(String)),
    [selectedAdditionalIds],
  );

  return (
    <section className="cqd2">
      <div className="cqd2__grid">
        {/* Form */}
        <section className="cqd2-card">
          <div className="cqd2-card__header">
            <HiOutlineClipboardList />
            <span>Consulta: {title}</span>
          </div>
          <div className="cqd2-card__body">
            <div className="cqd2__form">{children}</div>
          </div>
        </section>

        {/* Resumo */}
        <SummaryCard
          title={title}
          description={description}
          hasDescription={hasDescription}
        />
      </div>

      {hasAdds && (
        <InlineAddons
          options={additionalOptions}
          selectedSet={selectedSet}
          selectedAdditionalIds={selectedAdditionalIds}
          onChangeAdditionalIds={onChangeAdditionalIds}
        />
      )}
    </section>
  );
}

const getAddonCategory = (opt) => {
  const label =
    opt?.queryCategory ||
    opt?.category ||
    opt?.categoryName ||
    opt?.Category?.name ||
    opt?.meta?.category ||
    "Sem categoria";
  return String(label).trim() || "Sem categoria";
};

const parseCurrencyValue = (value) => {
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value !== "string") return 0;

  const clean = value.replace(/[^\d,.-]/g, "").trim();
  if (!clean) return 0;

  const normalized =
    clean.includes(",") && clean.includes(".")
      ? clean.replace(/\./g, "").replace(",", ".")
      : clean.replace(",", ".");

  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatAddonPrice = (price) => {
  if (price === null || price === undefined || price === "")
    return "Sob consulta";
  const formatted = formatToReal(price);
  return formatted === "R$ -" ? "Sob consulta" : formatted;
};

function InlineAddons({
  options = [],
  selectedSet: externalSelectedSet,
  selectedAdditionalIds = [],
  onChangeAdditionalIds = () => {},
}) {
  const selectedSet = useMemo(() => {
    if (externalSelectedSet && typeof externalSelectedSet.has === "function")
      return externalSelectedSet;
    return new Set((selectedAdditionalIds || []).map(String));
  }, [externalSelectedSet, selectedAdditionalIds]);

  const items = useMemo(
    () =>
      (options || []).map((opt) => ({
        id: opt?.id,
        name: opt?.name || "",
        category: getAddonCategory(opt),
        description: opt?.description || "",
        price: opt?.price,
        priceValue: parseCurrencyValue(opt?.price),
        priceLabel: formatAddonPrice(opt?.price),
        disabled: Boolean(opt?.disabled),
      })),
    [options],
  );

  const [search, setSearch] = useState("");

  const visibleItems = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return items;

    return items.filter((item) =>
      `${item.name} ${item.description} ${item.category}`
        .toLowerCase()
        .includes(term),
    );
  }, [items, search]);

  const groupedItems = useMemo(() => {
    const map = new Map();
    visibleItems.forEach((item) => {
      const category = item.category || "Sem categoria";
      if (!map.has(category)) map.set(category, []);
      map.get(category).push(item);
    });

    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b, "pt-BR", { sensitivity: "base" }))
      .map(([category, groupItems]) => ({
        category,
        items: groupItems.sort((x, y) =>
          String(x.name || "").localeCompare(String(y.name || ""), "pt-BR", {
            sensitivity: "base",
          }),
        ),
      }));
  }, [visibleItems]);

  const toggle = (id) => {
    const idStr = String(id);
    const current = new Set((selectedAdditionalIds || []).map(String));
    if (current.has(idStr)) current.delete(idStr);
    else current.add(idStr);
    onChangeAdditionalIds(Array.from(current));
  };

  const [isCollapsed, setIsCollapsed] = useState(false);
  const selectedItems = useMemo(
    () => items.filter((item) => selectedSet.has(String(item.id))),
    [items, selectedSet],
  );
  const selectedCount = selectedItems.length;
  const selectedTotal = selectedItems.reduce(
    (sum, item) => sum + item.priceValue,
    0,
  );

  return (
    <div className="cqd2-addons cqd2-addons--full shadow-sm">
      {/* ── Cabeçalho ── */}
      <div className="cqd2-addons__header">
        <div className="cqd2-addons__header-left">
          <div className="cqd2-addons__icon-wrap">
            <HiOutlineSparkles />
          </div>
          <div>
            <div className="cqd2-addons__title">Adicionais da consulta</div>
            <div className="cqd2-addons__subtitle">
              Dados complementares sob demanda
            </div>
          </div>
        </div>
        <div className="cqd2-addons__header-right">
          <div className="cqd2-addons__summary">
            <span className="cqd2-addons__badge">
              {selectedCount}{" "}
              {selectedCount === 1 ? "selecionado" : "selecionados"}
            </span>
            <span className="cqd2-addons__total">
              <HiOutlineCash />
              <span>Total</span>
              <strong>{formatToReal(selectedTotal)}</strong>
            </span>
          </div>
          <button
            type="button"
            className="cqd2-addons__toggle"
            aria-expanded={!isCollapsed}
            onClick={() => setIsCollapsed((prev) => !prev)}
          >
            {isCollapsed ? (
              <>
                <HiChevronRight />
                <span>Mostrar</span>
              </>
            ) : (
              <>
                <HiChevronDown />
                <span>Ocultar</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* ── Corpo ── */}
      {!isCollapsed && (
        <div className="cqd2-addons__body">
          <div className="cqd2-addons__toolbar">
            <label className="cqd2-addons__search">
              <HiOutlineSearch />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar adicional"
                aria-label="Buscar adicional"
              />
            </label>
            <div className="cqd2-addons__meta">
              <span>{items.length} disponíveis</span>
              <span>{visibleItems.length} exibidos</span>
            </div>
          </div>

          {groupedItems.length === 0 && (
            <div className="cqd2__empty">Nenhum adicional encontrado.</div>
          )}

          {groupedItems.map((group) => (
            <div key={group.category} className="cqd2-addons__section">
              <div className="cqd2-addons__section-head">
                <span className="cqd2-addons__section-dot" />
                <span className="cqd2-addons__section-title">
                  {group.category}
                </span>
              </div>
              <div className="cqd2-addons__list">
                {group.items.map((opt) => {
                  const checked = selectedSet.has(String(opt.id));
                  const disabled = Boolean(opt.disabled);
                  return (
                    <div
                      key={opt.id}
                      className={`cqd2-addon ${checked ? "is-active" : ""} ${
                        disabled ? "is-disabled" : ""
                      }`}
                      role="checkbox"
                      aria-checked={checked}
                      aria-disabled={disabled}
                      tabIndex={disabled ? -1 : 0}
                      onClick={() => !disabled && toggle(opt.id)}
                      onKeyDown={(e) => {
                        if (disabled) return;
                        if (e.key === " " || e.key === "Enter") {
                          e.preventDefault();
                          toggle(opt.id);
                        }
                      }}
                    >
                      <div className="cqd2-addon__top">
                        <span className="cqd2-addon__check">
                          {checked && <HiCheck />}
                        </span>
                        <span className="cqd2-addon__content">
                          <span className="cqd2-addon__name">{opt.name}</span>
                        </span>
                        <span className="cqd2-addon__price">
                          {opt.priceLabel === "Sob consulta"
                            ? opt.priceLabel
                            : `+ ${opt.priceLabel}`}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SummaryCard({ title, description, hasDescription }) {
  return (
    <section className="cqd2-card">
      <div className="cqd2-card__header">
        <HiOutlineViewGrid />
        <span>Resumo</span>
      </div>
      <div className="cqd2-card__body cqd2-card__body--summary">
        {hasDescription ? (
          <div className="cqd2__desc">
            <div className="cqd2__desc-title">{title}</div>
            <p>Descrição: {description.trim()}</p>
          </div>
        ) : (
          <div className="cqd2__empty cqd2__empty--rich">
            <div className="cqd2__desc-title">{title}</div>
            <p className="cqd2__empty-text">
              Aproveite as informações para tomar decisões rápidas e confiáveis
              enquanto entregamos insights com base no melhor sistema de
              consultas do mercado.
            </p>
          </div>
        )}

        {/* {hasTags && (
          <>
            <div className="cqd2__divider">O que retorna</div>
            <TagCloud tags={tags} max={12} />
          </>
        )} */}
      </div>
    </section>
  );
}
